import { FC, ReactNode, useMemo } from 'react';
import { Typography, styled } from '@mui/material';
import { FocusTheme } from '@/components/Focus/configs/theme';
import { TerritoryCode } from '@/components/Focus/utils/newScheduleAvails';
import { useFormatAvailDisplayLabel } from '@/components/Focus/hooks/useFormatAvailDisplayLabel';
import { ScheduleAvailType } from '@/redux/slices/slice_types';
import NewScheduleAvailItem, { NewScheduleAvailItemChangeType } from '../../NewScheduleAvails/NewScheduleAvailItem';
import { territoryGrouping } from '@/components/Focus/constants';
import AvailListContainer, { StyledChildren, StyledScroll } from '../../NewScheduleAvails/AvailListContainer';
import TerritoryGroupedAvailList, { GroupRights } from '../../NewScheduleAvails/TerritoryGroupAvailList';

type IncrementalSelectedAvailListProps = {
  originalRights: string[];
  addRights: string[];
  removeRights: string[];
  rightsOptions?: string[];
  type: ScheduleAvailType;
  heading?: ReactNode;
  headerRight?: ReactNode;
  onItemToggle?: (right: string) => void;
  disableTakedown?: boolean;
};

const getEntityLabel = (type: ScheduleAvailType, count: number): string => {
  if (type === 'territories') {
    return count === 1 ? 'territory' : 'territories';
  }

  return count === 1 ? 'language' : 'languages';
};

const getDisplayedRights = (originalRights: string[], addRights: string[]): TerritoryCode[] => {
  const seen = new Set<string>();
  const displayedRights: string[] = [];

  addRights.forEach((right) => {
    if (seen.has(right)) return;
    seen.add(right);
    displayedRights.push(right);
  });

  originalRights.forEach((right) => {
    if (seen.has(right)) return;
    seen.add(right);
    displayedRights.push(right);
  });

  return displayedRights as TerritoryCode[];
};

const getSelectedRights = (originalRights: string[], addRights: string[], removeRights: string[]): string[] => {
  const originalSelectedRights = originalRights.filter((right) => !removeRights.includes(right));
  const addedSelectedRights = addRights.filter((right) => !originalSelectedRights.includes(right));

  return [...originalSelectedRights, ...addedSelectedRights];
};

const getChangeType = (right: string, addRights: string[], removeRights: string[]): NewScheduleAvailItemChangeType => {
  if (addRights.includes(right)) return 'added';
  if (removeRights.includes(right)) return 'removed';
  return 'unchanged';
};

const getDerivedGroupedTerritories = (originalRights: string[], addRights: string[], removeRights: string[]): GroupRights[] => {
  return Object.entries(territoryGrouping)
    .map(([label, regionItems]) => {
      const regionCodes = regionItems.map((item) => item.code);

      const originalInGroup = originalRights.filter((right) => regionCodes.includes(right as TerritoryCode));
      const addInGroup = addRights.filter((right) => regionCodes.includes(right as TerritoryCode));
      const removeInGroup = removeRights.filter((right) => regionCodes.includes(right as TerritoryCode));

      const displayedTerritories = getDisplayedRights(originalInGroup, addInGroup);
      const selectedTerritories = getSelectedRights(originalInGroup, addInGroup, removeInGroup);

      return {
        label,
        territories: displayedTerritories,
        selectedCount: selectedTerritories.length,
        addedCount: addInGroup.length,
        removedCount: removeInGroup.length
      };
    })
    .filter((group) => group.territories.length > 0);
};

const IncrementalSelectedAvailList: FC<IncrementalSelectedAvailListProps> = ({
  originalRights,
  addRights,
  removeRights,
  rightsOptions,
  type,
  heading,
  headerRight,
  onItemToggle,
  disableTakedown
}) => {
  const isTerritoriesWithGroups = type === 'territories';

  const formatDisplayLabel = useFormatAvailDisplayLabel();
  const formatDisplayLabelForType = useMemo(() => (code: string) => formatDisplayLabel(code, type), [formatDisplayLabel, type]);

  const displayedRights = useMemo(() => getDisplayedRights(originalRights, addRights), [originalRights, addRights]);

  const selectedRights = useMemo(() => getSelectedRights(originalRights, addRights, removeRights), [originalRights, addRights, removeRights]);

  const groupedDisplayedTerritories = useMemo(() => {
    if (!isTerritoriesWithGroups) return [];
    return getDerivedGroupedTerritories(originalRights, addRights, removeRights);
  }, [originalRights, addRights, removeRights, isTerritoriesWithGroups]);

  const selectedCount = selectedRights.length;
  const defaultHeading = `Selected • ${selectedCount} ${getEntityLabel(type, selectedCount)}`;

  const defaultHeaderRight =
    addRights.length || removeRights.length ? (
      <StyledHeaderRight>
        {!!addRights.length && <StyledAddedCount variant="h5">{`+${addRights.length}`}</StyledAddedCount>}
        {!!removeRights.length && <StyledRemovedCount variant="h5">{`(-${removeRights.length})`}</StyledRemovedCount>}
      </StyledHeaderRight>
    ) : null;

  return (
    <AvailListContainer heading={heading ?? defaultHeading} headerRight={headerRight ?? defaultHeaderRight}>
      <StyledScroll data-type={type}>
        {isTerritoriesWithGroups ? (
          <StyledChildren>
            <TerritoryGroupedAvailList
              groups={groupedDisplayedTerritories}
              formatDisplayLabel={formatDisplayLabelForType}
              hasRights={(code) => !!rightsOptions?.includes(code)}
              isSelected={() => false}
              onItemClick={(code) => onItemToggle?.(code)}
              onItemsClick={(codes) => codes.forEach((code) => onItemToggle?.(code))}
              variant="removable"
              getItemChangeType={(code) => getChangeType(code, addRights, removeRights)}
              showCheckbox
              hideSelectAll
            />
          </StyledChildren>
        ) : (
          <StyledChildren>
            {displayedRights.map((right) => (
              <NewScheduleAvailItem
                key={right}
                onClick={() => onItemToggle?.(right)}
                primaryLabel={formatDisplayLabelForType(right)}
                type="success"
                variant="removable"
                isSelectedItem={false}
                hasRights={!!rightsOptions?.includes(right)}
                changeType={getChangeType(right, addRights, removeRights)}
                showCheckbox
                disableTakedown={disableTakedown}
              />
            ))}
          </StyledChildren>
        )}
      </StyledScroll>
    </AvailListContainer>
  );
};

export default IncrementalSelectedAvailList;

const StyledHeaderRight = styled('div')`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1)};
`;

const StyledAddedCount = styled(Typography)`
  color: ${({ theme }) => (theme as FocusTheme).palette.primary.main};
  font-weight: 700;
`;

const StyledRemovedCount = styled(Typography)`
  color: ${({ theme }) => (theme as FocusTheme).palette.neutral[20]};
  font-weight: 700;
`;
